import useCountryDetailsStore from '@/context/CountryDetailsStore';
import useRegionStore from '@/context/RegionStore';
import { currencySymbol, formatCurrency, roundToCents } from '@/utils/priceFormatting';
import { useEffect, useRef } from 'react';

interface CurrencyInfo {
    symbol: string;
    code: string;
    name: string;
    formatPrice: (amount: number, convertToCents: boolean) => string;
    formatPriceFixed: (amount: number, convertToCents: boolean) => string;
    freeDeliveryThreshold: number | null;
    freeDeliveryThresholdFormatted: string | null;
    isLoading: boolean;
}

export const useCurrency = (): CurrencyInfo => {
    const { countryDetails, fetchDetails, isLoading } = useCountryDetailsStore();
    const { chosenRegion } = useRegionStore();
    const lastFetchedRegion = useRef<string | null>(null);

    const defaultCurrency = {
        symbol: '£',
        code: 'GBP',
        name: 'British Pound'
    };

    // Fetch details for the chosen region (only if different from last fetch)
    useEffect(() => {
        if (chosenRegion && chosenRegion !== lastFetchedRegion.current) {
            lastFetchedRegion.current = chosenRegion;
            // Don't throw errors - let RegionWrapper handle unsupported regions
            fetchDetails(chosenRegion).catch(() => {
                // Reset ref on failure to allow retry when user switches back to this region
                if (lastFetchedRegion.current === chosenRegion) {
                    lastFetchedRegion.current = null;
                }
            });
        }
    }, [chosenRegion, fetchDetails]);

    const currency = countryDetails?.currency || defaultCurrency;
    // console.log('currency', currency)
    // console.log('countryDetails', countryDetails)

    const formatPrice = (amount: number, convertToCents: boolean): string => {
        // Convert amount from cents/pence to actual currency
        const actualAmount = convertToCents ? amount / 100 : amount;
        const roundedAmount = roundToCents(actualAmount);
        const hasFraction = Math.abs(roundedAmount % 1) > Number.EPSILON;
        const fractionDigits = hasFraction ? 2 : 0;

        return formatCurrency(roundedAmount, currency.code, { minimumFractionDigits: fractionDigits });
    };

    const formatPriceFixed = (amount: number, convertToCents: boolean): string => {
        const actualAmount = convertToCents ? amount / 100 : amount;
        const roundedAmount = roundToCents(actualAmount);

        return formatCurrency(roundedAmount, currency.code, { minimumFractionDigits: 2 });
    };

    return {
        ...currency,
        symbol: currencySymbol(currency.code),
        formatPrice,
        formatPriceFixed,
        freeDeliveryThreshold: countryDetails?.freeDeliveryThreshold ?? null,
        freeDeliveryThresholdFormatted: countryDetails?.freeDeliveryThresholdFormatted ?? null,
        isLoading
    };
};
